import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClient } from '@/auth';
import type { RequestBody } from '@http';
import type { MarketplaceCountryCode } from '@Constants/marketplaces.constants';
import type { ProductAnalysisResponse } from '../api/searchNicheApi';
import { chargeTokens } from '../utils/chargeTokens';

export type { MarketplaceCountryCode as MarketplaceCode };

// Filters interface for UI components
export interface ProductIdeasFilters {
    search?: string;
    marketplace?: MarketplaceCountryCode;
    status?: 'active' | 'inactive' | 'archived';
    sort_by?: 'date_asc' | 'date_desc' | 'price_asc' | 'price_desc' | 'revenue_desc' | 'sales_desc';
    niche_names?: string[];
}

export interface ProductIdeasQueryParams {
    page?: number;
    page_size?: number;
    search?: string;
    marketplace?: MarketplaceCountryCode;
    status?: 'active' | 'inactive' | 'archived';
    sort_by?: 'date_asc' | 'date_desc' | 'price_asc' | 'price_desc' | 'revenue_desc' | 'sales_desc';
    include_snapshots?: boolean;
    niche_names?: string[];
}

export interface NichesResponse {
    niches: string[];
}

export interface ProductSnapshot {
    product_name: string;
    asin: string;
    product_url: string;
    product_desc?: string | null;
    price: number;
    price_currency?: string | null;
    rating?: number;
    reviews_summary?: string | null;
    product_score?: number;
    images_urls: string[];
    rank_history?: any | null;
    sales_history?: any | null;
    bsr_category_data?: Array<{
        rank: number;
        category_id: string;
        category_name: string;
    }>;
    metrics?: {
        approximate_30_day_units_sold?: number;
        approximate_30_day_revenue?: number;
        listing_quality_score?: number;
        rating?: number;
        reviews?: number;
        price?: number;
        amz_ref_fee?: number;
        amz_fba_fee?: number;
        all_amazon_fees?: number;
        all_amazon_fees_percentage?: number;
        est_COGS_percentage?: number;
        est_COGS?: number;
        sales?: number;
        sales_per_day?: number;
        sales_change_1y?: number;
        sales_change_3m?: number;
        sales_change_6m?: number;
        variations_count?: number;
        number_of_sellers?: number;
        price_new_current?: number;
        price_list_current?: number;
        price_used_current?: number;
        referral_fee_percent?: number;
        image_count?: number;
        listing_age?: number;
        width_value?: number;
        height_value?: number;
        length_value?: number;
        weight_value?: number;
        bsr_in_electronics?: number;
        bsr_in_mp?: number;
        bsr_in_home_audio?: number;
        [key: string]: number | undefined;
    };
    tags?: {
        [key: string]: string;
    } | null;
    analysisTags?: string;
}

export interface LatestSnapshot {
    created_datetime: string;
    product_data: ProductSnapshot;
}

export interface ProductIdeasItem {
    id: string;
    asin: string;
    analysis_id: number;
    niche_id?: string | null;
    niche_name?: string | null;
    niche_score?: number | null;
    niche_summary?: string | null;
    status: 'active' | 'inactive' | 'archived' | 'deleted';
    comment?: string | null;
    added_at: string;
    product_score?: number;
    llm_insight?: boolean;
    latest_snapshot?: LatestSnapshot;
}

// API response interface
export interface ProductIdeasResponse {
    items: ProductIdeasItem[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

// Base query arguments interface
interface BaseQueryArgs {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: RequestBody;
    params?: Record<string, unknown>;
}

// Wrapper to use our apiClient instead of fetchBaseQuery
const apiClientBaseQuery = async (args: BaseQueryArgs) => {
    const { url, method = 'GET', body, params } = args;
    
    try {
        let response;
        
        switch (method) {
            case 'GET':
                response = await apiClient.get(url, params);
                break;
            case 'POST':
                response = await apiClient.post(url, body ?? undefined);
                break;
            case 'PUT':
                response = await apiClient.put(url, body ?? undefined);
                break;
            case 'DELETE':
                response = await apiClient.delete(url);
                break;
            default:
                throw new Error(`Unsupported method: ${method}`);
        }
        
        return { data: response.data };
    } catch (error: unknown) {
        const apiError = error as { response?: { status?: number; data?: unknown }; message?: string };
        return {
            error: {
                status: apiError.response?.status || 500,
                data: apiError.response?.data || apiError.message,
            },
        };
    }
};

/**
 * Product Ideas API Slice
 * Provides automatic caching, invalidation, and optimistic updates
 */
export const productIdeasApi = createApi({
    reducerPath: 'productIdeasApi',
    baseQuery: apiClientBaseQuery,
    tagTypes: ['ProductIdeas', 'FavoriteCheck', 'Niches'],
    endpoints: (builder) => ({
        getNiches: builder.query<NichesResponse, void>({
            query: () => ({
                url: 'sellers-agents/products/niches',
                method: 'GET',
            }),
            providesTags: [{ type: 'Niches', id: 'LIST' }],
            keepUnusedDataFor: 300,
        }),

        getProductIdeas: builder.query<ProductIdeasResponse, ProductIdeasQueryParams | void>({
            query: (params) => ({
                url: 'sellers-agents/products',
                method: 'GET',
                params: {
                    page: params?.page ?? 1,
                    page_size: params?.page_size ?? 12,
                    sort_by: params?.sort_by ?? 'date_desc',
                    include_snapshots: params?.include_snapshots ?? true,
                    product_status: params?.status,
                    search: params?.search,
                    marketplace: params?.marketplace,
                    niche_names: params?.niche_names?.length ? params.niche_names : undefined,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map(({ id }) => ({ type: 'ProductIdeas' as const, id })),
                        { type: 'ProductIdeas', id: 'LIST' },
                    ]
                    : [{ type: 'ProductIdeas', id: 'LIST' }],
        }),

        /**
         * Get single product by ID
         */
        getProductById: builder.query<ProductIdeasItem, string>({
            query: (productId) => ({
                url: `sellers-agents/products/${productId}`,
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'ProductIdeas', id }],
        }),

        /**
         * Add product to favorites
         */
        addProductToFavorites: builder.mutation<
            ProductIdeasItem,
            {
                asin: string;
                product_data: ProductSnapshot;
                niche_id?: string;
                comment?: string;
                analysis_id?: number | string;
                niche_name?: string;
                niche_score?: number;
                niche_summary?: string;
                marketplace?: string;
            }
        >({
            query: (params) => ({
                url: 'sellers-agents/products',
                method: 'POST',
                body: params,
            }),
            invalidatesTags: [
                { type: 'ProductIdeas', id: 'LIST' },
                { type: 'FavoriteCheck', id: 'LIST' },
            ],
        }),

        /**
         * Update product status or comment
         */
        updateProduct: builder.mutation<
            ProductIdeasItem,
            {
                asin: string;
                status?: 'active' | 'inactive' | 'archived';
                comment?: string;
            }
        >({
            query: ({ asin, ...data }) => ({
                url: `sellers-agents/products/${asin}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: [{ type: 'ProductIdeas', id: 'LIST' }],
            async onQueryStarted({ asin, ...data }, { dispatch, queryFulfilled, getState }) {
                // Optimistic update — apply changes immediately
                const patchResults: Array<{ undo: () => void }> = [];
                const state = getState() as { productIdeasApi?: { queries?: Record<string, any> } };
                const queries = state.productIdeasApi?.queries || {};

                Object.keys(queries).forEach((key) => {
                    if (key.startsWith('getProductIdeas')) {
                        const queryKey = queries[key]?.originalArgs;

                        const patchResult = dispatch(
                            productIdeasApi.util.updateQueryData('getProductIdeas', queryKey, (draft) => {
                                const index = draft.items.findIndex(item => item.asin === asin);
                                if (index !== -1) {
                                    Object.assign(draft.items[index], data);
                                }
                            })
                        );
                        patchResults.push(patchResult);
                    }
                });

                try {
                    const { data: updatedProduct } = await queryFulfilled;
                    // Sync with full server response
                    Object.keys(queries).forEach((key) => {
                        if (key.startsWith('getProductIdeas')) {
                            const queryKey = queries[key]?.originalArgs;

                            dispatch(
                                productIdeasApi.util.updateQueryData('getProductIdeas', queryKey, (draft) => {
                                    const index = draft.items.findIndex(item => item.asin === asin);
                                    if (index !== -1) {
                                        draft.items[index] = {
                                            ...draft.items[index],
                                            ...updatedProduct,
                                            latest_snapshot: updatedProduct.latest_snapshot || draft.items[index].latest_snapshot,
                                        };
                                    }
                                })
                            );
                        }
                    });
                } catch {
                    // Rollback on error
                    patchResults.forEach(({ undo }) => undo());
                }
            },
        }),

        /**
         * Delete product
         */
        deleteProduct: builder.mutation<void, { id: string; asin: string }>({
            query: ({ asin }) => ({
                url: `sellers-agents/products/${asin}`,
                method: 'DELETE',
            }),
            invalidatesTags: [
                { type: 'ProductIdeas', id: 'LIST' },
                { type: 'FavoriteCheck', id: 'LIST' },
            ],
            async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
                const patchResults: Array<{ undo: () => void }> = [];
                
                try {
                    // Оптимистично удаляем из ВСЕХ закэшированных страниц
                    const state = getState() as { productIdeasApi?: { queries?: Record<string, any> } };
                    const queries = state.productIdeasApi?.queries || {};
                    
                    Object.keys(queries).forEach((key) => {
                        if (key.startsWith('getProductIdeas')) {
                            const queryKey = queries[key]?.originalArgs;
                            
                            const patchResult = dispatch(
                                productIdeasApi.util.updateQueryData('getProductIdeas', queryKey, (draft) => {
                                    const index = draft.items.findIndex(item => item.id === id);
                                    if (index !== -1) {
                                        draft.items.splice(index, 1);
                                    }
                                    draft.total = Math.max(0, draft.total - 1);
                                })
                            );
                            patchResults.push(patchResult);
                        }
                    });
                    
                    await queryFulfilled;
                } catch {
                    // Откат при ошибке
                    patchResults.forEach(({ undo }) => undo());
                }
            },
        }),

        /**
         * Refresh product data
         */
        refreshProduct: builder.mutation<LatestSnapshot, { id: string; asin: string }>({
            query: ({ asin }) => ({
                url: `sellers-agents/products/${asin}/refresh`,
                method: 'POST',
            }),
            invalidatesTags: [{ type: 'ProductIdeas', id: 'LIST' }],
            async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data: latestSnapshot } = await queryFulfilled;
                    // Обновляем latest_snapshot во ВСЕХ закэшированных страницах
                    const state = getState() as { productIdeasApi?: { queries?: Record<string, any> } };
                    const queries = state.productIdeasApi?.queries || {};
                    
                    Object.keys(queries).forEach((key) => {
                        if (key.startsWith('getProductIdeas')) {
                            const queryKey = queries[key]?.originalArgs;
                            
                            dispatch(
                                productIdeasApi.util.updateQueryData('getProductIdeas', queryKey, (draft) => {
                                    const index = draft.items.findIndex(item => item.id === id);
                                    if (index !== -1) {
                                        draft.items[index].latest_snapshot = latestSnapshot;
                                    }
                                })
                            );
                        }
                    });
                } catch {
                    // Ошибка обработана компонентом
                }
            },
        }),

        /**
         * Analyze product with LLM — returns cached report if already generated
         */
        analyzeProduct: builder.mutation<ProductAnalysisResponse, { analysisId: number; asin: string }>({
            query: ({ analysisId, asin }) => ({
                url: `sellers-agents/analysis/${analysisId}/products/${asin}/analyze`,
                method: 'POST',
            }),
            async onQueryStarted({ analysisId, asin }, { dispatch, queryFulfilled, getState }) {
                try {
                    await queryFulfilled;
                    const state = getState() as { productIdeasApi?: { queries?: Record<string, any> } };
                    const queries = state.productIdeasApi?.queries || {};

                    Object.keys(queries).forEach((key) => {
                        if (key.startsWith('getProductIdeas')) {
                            const queryKey = queries[key]?.originalArgs;

                            dispatch(
                                productIdeasApi.util.updateQueryData('getProductIdeas', queryKey, (draft) => {
                                    const item = draft.items.find(i => i.asin === asin);
                                    if (item) {
                                        item.llm_insight = true;
                                    }
                                })
                            );
                        }
                    });

                    // WE-115 fix: this mutation consumes tokens server-side but never
                    // invalidated the cached subscription usage, so the sidebar balance
                    // went stale after an insight analysis until an unrelated refetch.
                    chargeTokens(dispatch, 'insight', analysisId);
                } catch {
                    // Ошибка обработана компонентом
                }
            },
        }),

        /**
         * Check if products are in favorites
         * Cached for 5 minutes (300 seconds)
         */
        checkProductsInFavorites: builder.query<Record<string, boolean>, string[]>({
            query: () => ({
                url: 'sellers-agents/products',
                method: 'GET',
                params: {
                    page: 1,
                    page_size: 100,
                    include_snapshots: false,
                },
            }),
            transformResponse: (response: ProductIdeasResponse, meta, asins) => {
                const favoriteAsins = new Set(response.items.map(item => item.asin));
                return asins.reduce((acc, asin) => {
                    acc[asin] = favoriteAsins.has(asin);
                    return acc;
                }, {} as Record<string, boolean>);
            },
            providesTags: [{ type: 'FavoriteCheck', id: 'LIST' }],
            // Cache for 5 minutes
            keepUnusedDataFor: 300,
        }),
    }),
});

// Export hooks for usage in components
export const {
    useGetNichesQuery,
    useGetProductIdeasQuery,
    useLazyGetProductIdeasQuery,
    useGetProductByIdQuery,
    useAddProductToFavoritesMutation,
    useUpdateProductMutation,
    useDeleteProductMutation,
    useRefreshProductMutation,
    useAnalyzeProductMutation,
    useCheckProductsInFavoritesQuery,
    useLazyCheckProductsInFavoritesQuery,
} = productIdeasApi;
